import React, { useState } from 'react';
import { userSelectors, userOperations } from 'store/user';
import { User } from 'domain/User';
import { TextInput, Text, Button } from '@patternfly/react-core';
import { EyeIcon, EyeSlashIcon } from '@patternfly/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { usePageableData } from 'util/FunctionalComponentUtils';
import TypeaheadSelectInput from 'ui/components/TypeaheadSelectInput';
import LoadingSpinner from 'ui/components/LoadingSpinner';
import {
  DataTableUrlProps, RowEditButtons, RowViewButtons,
  setSorterInUrl, TableCell, TableRow, DataTable,
} from 'ui/components/DataTable';
import { tenantSelectors } from 'store/tenant';
import { Sorter } from 'types';
import { stringSorter } from 'util/CommonSorters';
import { getPropsFromUrl } from 'util/BookmarkableUtils';
import { useValidators } from 'util/ValidationUtils';
/* eslint-disable */

export type Props = RouteComponentProps;

export function UserRow({ user, setIsLoading }: { user: User; setIsLoading: (loading: boolean) => void }) {
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation('UserPage');

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await dispatch(userOperations.removeUser(user));
      await dispatch(userOperations.refreshUserList());
    } catch (error) {
      console.error("Error al eliminar usuario:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isEditing) {
    return (<EditableUserRow user={user} isNew={false} onClose={() => setIsEditing(false)} setIsLoading={setIsLoading}/>);
  }

  return (
    <TableRow>
      <TableCell columnName={t('email')}>
        <Text>{user.email}</Text>
      </TableCell>
      <TableCell columnName={t('role')}>
        <Text>{user.role}</Text>
      </TableCell>
      <RowViewButtons
        onEdit={() => setIsEditing(true)}
        onDelete={handleDelete}
      />
    </TableRow>
  );
}

export function EditableUserRow(props: { user: User; isNew: boolean; onClose: () => void ; setIsLoading: (loading: boolean) => void;}) {
  const [email, setEmail] = useState(props.user.email);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState(props.user.role);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const roleList = [{name: 'USER'}, {name: 'ADMIN'}];
  const userList = useSelector(userSelectors.getUserList);
  const dispatch = useDispatch();
  const { t } = useTranslation('UsersPage');

  const validators = {
    emailMustNotBeEmpty: {
      predicate: (user: User) => user.email.length > 0,
      errorMsg: () => t('EmailEmptyUserError'),
    },
    emailAlreadyTaken: {
      predicate: (user: User) => userList.filter((otherUser) => otherUser.email === user.email
        && otherUser.id !== user.id).length === 0,
      errorMsg: (user: User) => t('userEmailAlreadyTakenError', { email: user.email }),
    },
    emailMustBeValid: {
      predicate: (user: User) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email),
      errorMsg: () => t('InvalidEmailError'),
    },
    roleMustNotBeEmpty: {
      predicate: (user: User) => user.role.length > 0,
      errorMsg: () => t('RoleEmptyUserError'),
    },
    passwordMustNotBeEmpty:{
      predicate: (user: User) => props.isNew || isChangingPassword ? user.password.length > 0 : true,
      errorMsg: () => t('PasswordEmptyUserError'),
    }
  };

  const validationErrors = useValidators({
    ...props.user,
    email,
    role,
    password,
  }, validators);

  const handleSave = async () => {
    props.setIsLoading(true);
    try {
      if (props.isNew) {
        await dispatch(userOperations.addUser({ ...props.user, email, role, password }));
      } else {
        await dispatch(userOperations.updateUser({ 
          ...props.user, email, role, ...(isChangingPassword && { password }) 
        }));
      }
      await dispatch(userOperations.refreshUserList());
    } catch (error) {
      console.error('Error al guardar usuario:', error);
    } finally {
      props.setIsLoading(false);
    }
  };

  return (
    <TableRow>
      <TableCell columnName={t('email')}>
        <TextInput value={email} onChange={setEmail} type='email' placeholder={t('email')}  />
        {validationErrors.showValidationErrors('emailMustNotBeEmpty', 'emailAlreadyTaken', 'emailMustBeValid')}
      </TableCell>
      <TableCell columnName={t('role')}>
        <TypeaheadSelectInput
          value={roleList.find((r) => r.name === role)}
          options={roleList}
          optionToStringMap={(newrole) => newrole.name}
          onChange={(newrole) => {
            if (newrole) {
              setRole(newrole.name);
            }
          }}
          noClearButton
          emptyText={t('selectARole')}
        />
        {validationErrors.showValidationErrors('roleMustNotBeEmpty')}
      </TableCell>
      <TableCell columnName={t('password')}>
        {(isChangingPassword || props.isNew) ? (
          <>
            <div style={{ position: 'relative' }}>
              <TextInput 
                value={password} 
                onChange={setPassword} 
                placeholder={t('enterPassword')} 
                type={showPassword ? 'text' : 'password'}
                style={{ borderRadius: '0.5rem', paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword ? (
                  <EyeSlashIcon style={{ width: '1.25rem', height: '1.25rem', color: '#6a6e73' }} />
                ) : (
                  <EyeIcon style={{ width: '1.25rem', height: '1.25rem', color: '#6a6e73' }} />
                )}
              </button>
              {validationErrors.showValidationErrors('passwordMustNotBeEmpty')}
            </div>
          </>
        ) : (
          !props.isNew && ( // Mostrar el botón solo para edición
            <Button onClick={() => setIsChangingPassword(true)} variant='secondary'>
              {t('changePassword')}
            </Button>
          )
        )}
      </TableCell>
     
      <RowEditButtons
        isValid={validationErrors.isValid}
        onSave={handleSave}
        onClose={() => props.onClose()}
      />
    </TableRow>
  );
}

export const UsersPage: React.FC<Props> = (props) => {
  const userList = useSelector(userSelectors.getUserList);
  const tenantId = useSelector(tenantSelectors.getTenantId);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation('UsersPage');

  const columns = [
    { name: t('email'), sorter: stringSorter<User>((user) => user.email) },
    { name: t('role') },
  ];

  const urlProps = getPropsFromUrl<DataTableUrlProps>(props, {
    page: '1',
    itemsPerPage: '10',
    filter: null,
    sortBy: '0',
    asc: 'true',
  });

  const sortBy = parseInt(urlProps.sortBy || '-1', 10);
  const sorter = columns[sortBy].sorter as Sorter<User>;;

  const pageableData = usePageableData(urlProps, userList, (user) => [user.email], sorter);

  return (
    <>
      <LoadingSpinner isLoading={isLoading} />
      <DataTable
        {...props}
        {...pageableData}
        title={t('users')}
        columns={columns}
        rowWrapper={(user) => (<UserRow key={user.id} user={user} setIsLoading={setIsLoading} />)}
        sortByIndex={sortBy}
        onSorterChange={(index) => setSorterInUrl(props, urlProps, sortBy, index)}
        newRowWrapper={(removeRow) => (
          <EditableUserRow
            isNew
            onClose={removeRow}
            user={{
              tenantId,
              email: '',
              password: '',
              role: '',
            }}
            setIsLoading={setIsLoading}
          />
        )}
      />
    </>
  );
};

export default withRouter(UsersPage);
